/**
 * Phase 4g.E (Feb 2026) — Auftrags-Teilen-Modal extrahiert aus
 * `WorkshopOrdersPage.jsx`. Erlaubt einem Händler, einen Werkstatt-Auftrag
 * an einen anderen Händler weiterzugeben (Kapazitäts-Sharing).
 * Jun-2026: Ziel-Händler wird aus einer Auswahlliste registrierter B2B-Händler
 * gewählt (statt freier E-Mail-Eingabe).
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

function ShareOrderModal({ order, onClose, onShared }) {
    const [dealers, setDealers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [query, setQuery] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dealers/share-directory");
                setDealers(data.dealers || []);
            } catch {
                setDealers([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = dealers.filter((d) =>
        !query.trim() ||
        (d.label || "").toLowerCase().includes(query.toLowerCase()) ||
        (d.email || "").toLowerCase().includes(query.toLowerCase()));

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) { toast.error("Bitte einen Ziel-Händler auswählen"); return; }
        setBusy(true);
        try {
            await api.post(`/work-orders/${order.id}/share`, {
                target_dealer_email: email.trim(),
                note: note.trim() || null,
            });
            toast.success(`Auftrag an ${email} weitergeleitet`);
            onShared();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="share-modal">
            <div className="bg-white max-w-lg w-full rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <div>
                        <div className="font-display font-black text-lg">Auftrag teilen · {order.order_number}</div>
                        <div className="text-[11px] text-gray-500">Kapazitäts-Sharing mit anderem Händler</div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="share-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Ziel-Händler *</label>
                        {loading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-2" data-testid="share-loading">
                                <Loader2 className="w-4 h-4 animate-spin" /> Händler werden geladen…
                            </div>
                        ) : dealers.length === 0 ? (
                            <p className="text-sm text-gray-600 mt-2">Keine weiteren registrierten B2B-Händler gefunden.</p>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Suchen (Name, Ort, E-Mail)…"
                                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-[#FFCC00] outline-none"
                                    data-testid="share-dealer-search"
                                />
                                <select
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    size={Math.min(6, Math.max(3, filtered.length))}
                                    className="w-full rounded-sm border border-gray-300 px-2 py-1 text-sm mt-2 focus:ring-2 focus:ring-[#FFCC00] outline-none"
                                    data-testid="share-dealer-select"
                                >
                                    <option value="" disabled>— Händler auswählen —</option>
                                    {filtered.map((d) => (
                                        <option key={d.id} value={d.email}>{d.label} ({d.email})</option>
                                    ))}
                                </select>
                            </>
                        )}
                        <p className="text-[10px] text-gray-500 mt-1">Nur registrierte TwoWheels-Händler (Rolle B2B) werden angezeigt.</p>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Notiz (optional)</label>
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="z. B. ‚Brauche schnell Hilfe, eigene Hebebühne defekt'"
                            rows={3}
                            className="rounded-sm mt-1"
                            data-testid="share-note"
                        />
                    </div>
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-sm text-xs text-amber-900">
                        Beim Annehmen wird beim Ziel-Händler eine eigene Kopie angelegt (mit Fahrzeug- und Positionsdaten). Den Kunden-Datensatz muss der Ziel-Händler selbst anlegen (Datenschutz).
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" onClick={onClose} variant="outline" className="rounded-sm">Abbrechen</Button>
                        <Button type="submit" disabled={busy || !email} className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="share-submit">
                            {busy ? "Sende…" : "Anfrage senden"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ShareOrderModal;
